import React from "react"
import { cn } from "@/lib/utils"

/**
 * Separator - Visual divider between content sections
 * 
 * API zgodne z Radix UI Separator
 * - Orientation: "horizontal" | "vertical"
 * - Decorative: boolean (czy jest tylko wizualny czy semantic)
 * 
 * @example
 * ```tsx
 * <Separator />
 * <Separator orientation="vertical" className="h-6" />
 * <Separator decorative />
 * ```
 */

export interface SeparatorProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Orientation of the separator
   * @default "horizontal"
   */
  orientation?: "horizontal" | "vertical"
  /**
   * Whether the separator is purely decorative.
   * When true, it's hidden from screen readers.
   * @default true
   */
  decorative?: boolean
}

const Separator = React.forwardRef<HTMLDivElement, SeparatorProps>(
  (
    {
      className,
      orientation = "horizontal",
      decorative = true,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        role={decorative ? "none" : "separator"}
        aria-orientation={decorative ? undefined : orientation}
        className={cn(
          "shrink-0 bg-[var(--dark-a6)]",
          orientation === "horizontal"
            ? "h-[1px] w-full"
            : "h-full w-[1px]",
          className
        )}
        {...props}
      />
    )
  }
)
Separator.displayName = "Separator"

export { Separator }

